/**
 * WriteoffSlice Notes Reducers
 * 
 * Reducers for managing writeoff notes.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { WriteoffNoteView } from '../../types';
import { WriteoffSliceState } from '../state';
import { addEditedField, createBackupManager } from '../../../shared/sliceUtils';

// Backup manager for HMR recovery
const backupManager = createBackupManager<{
  writeoff: WriteoffSliceState['currentWriteoff'];
  isEdited: boolean;
  editedFields: string[];
}>('writeoff_backup');

/**
 * Helper to persist state to session storage
 */
function persistToSessionStorage(state: WriteoffSliceState) {
  if (state.currentWriteoff) {
    backupManager.persist({
      writeoff: state.currentWriteoff,
      isEdited: state.isEdited,
      editedFields: state.editedFields,
    });
  }
}

// =============================================================================
// NOTES REDUCERS
// =============================================================================

/**
 * Add a new note to the writeoff
 */
export const addWriteoffNote: CaseReducer<
  WriteoffSliceState,
  PayloadAction<Omit<WriteoffNoteView, 'id'> & { id?: string }>
> = (state, action) => {
  if (state.currentWriteoff) {
    const newNote: WriteoffNoteView = {
      ...action.payload,
      id: action.payload.id || `temp_${Date.now()}`,
    };
    state.currentWriteoff.notes.push(newNote);
    addEditedField(state, 'notes');
    persistToSessionStorage(state);
  }
};

/**
 * Update an existing note
 */
export const updateWriteoffNote: CaseReducer<
  WriteoffSliceState,
  PayloadAction<{ noteId: string; updates: Partial<WriteoffNoteView> }>
> = (state, action) => {
  if (state.currentWriteoff) {
    const noteIndex = state.currentWriteoff.notes.findIndex(
      (note) => note.id === action.payload.noteId
    );
    if (noteIndex !== -1) {
      state.currentWriteoff.notes[noteIndex] = {
        ...state.currentWriteoff.notes[noteIndex],
        ...action.payload.updates,
      };
      addEditedField(state, 'notes');
      persistToSessionStorage(state);
    }
  }
};

/**
 * Remove a note by ID
 */
export const removeWriteoffNote: CaseReducer<WriteoffSliceState, PayloadAction<string>> = (
  state,
  action
) => {
  if (state.currentWriteoff) {
    state.currentWriteoff.notes = state.currentWriteoff.notes.filter(
      (note) => note.id !== action.payload
    );
    addEditedField(state, 'notes');
    persistToSessionStorage(state);
  }
};
